import type { ShellSurface } from './shell-types.js';

/**
 * Where the primary button takes the shopper next — Zazzle's guided flow.
 *
 * The reference walks a multi-sided product one side at a time ("Next: Design
 * Back"), then the product options ("Next: Options"), then the review. Every
 * side stays reachable from the side thumbnails; the button only suggests the
 * order, it never locks one.
 *
 * `stage` is where the shopper is now: designing, choosing options, reviewing.
 */
export type DesignerStage = 'design' | 'options' | 'review';

export type DesignerStep =
  | { kind: 'surface'; id: string; name: string }
  | { kind: 'options' }
  | { kind: 'review' }
  | { kind: 'done' };

export function nextDesignerStep(
  stage: DesignerStage,
  surfaces: readonly ShellSurface[],
  hasOptions: boolean,
): DesignerStep {
  if (stage === 'review') return { kind: 'done' };
  if (stage === 'design') {
    const at = surfaces.findIndex((surface) => surface.current);
    const next = at >= 0 ? surfaces[at + 1] : undefined;
    if (next) return { kind: 'surface', id: next.id, name: next.name };
    if (hasOptions) return { kind: 'options' };
  }
  return { kind: 'review' };
}

/**
 * The phone has no Pregled tab in its top bar: GOTOVO runs the same native
 * preflight the desktop review does. So its flow is the sides, the options,
 * then done.
 */
export function nextPhoneStep(
  surfaces: readonly ShellSurface[],
  hasOptions: boolean,
  optionsSeen: boolean,
): DesignerStep {
  const step = nextDesignerStep('design', surfaces, hasOptions && !optionsSeen);
  return step.kind === 'review' ? { kind: 'done' } : step;
}
